import React from 'react';
import P from 'prop-types';
import cx from 'classnames';
import { graphql, compose } from 'react-apollo';

import { ThemeContext } from 'components/App/';
import Button from 'components/Button/';
import { PARTNERSHIP_STATUS_QUERY } from 'gql/queries';
import { REQUEST_PARTNERSHIP_MUTATION } from 'gql/mutations';
import { alertError, alertSuccess } from 'utils/alert';
import { partnershipStatus } from 'utils/partnership';
import displayLoading from 'utils/displayLoading';

import './PartnershipStatus.scss';

const STATUS = {
  approved:   'approved',
  pending:    'pending',
  cancelled:  'cancelled',
  rejected:   'rejected',
};

const onSuccess = () => alertSuccess('Partnership has been requested');

const PartnershipStatus = ({
  data,
  requesterId,
  requesteeId,
  requestPartnershipMutation,
  className,
  longMessage,
}) => {
  const status = partnershipStatus({
    partnerships: data.store.group.partnerships,
    requesterId,
    requesteeId,
  });

  const params = {
    variables: {
      input: {
        requesterId,
        requesteeId,
        reason: 'Automated partnership request from website',
      },
    },
  };

  const classname = cx('PartnershipStatus', className);

  return (
    <ThemeContext.Consumer>
      {
        ({ name: VRCName }) => {

          if      (status === STATUS.approved) {
            return null;
          }
          else if (status === STATUS.pending) {
            return (
              <div className={classname}>
                {
                  !longMessage ? 'Partnership pending' :
                  (
                    <span>
                      Your partnership with <b>{VRCName}</b> is pending, and will be reviewed in the coming days.
                      Please contact {VRCName} if you have any questions. 
                    </span>
                  )
                }
              </div>
            );
          }
          else if (status === STATUS.cancelled) {
            return (
              <div className={classname}>
                <div>
                  {
                    !longMessage ? <span>Partnership <i>cancelled</i></span> :
                    (
                      <span>
                        Your partnership with <b>{VRCName}</b> has been cancelled by someone in your organisation <i><b>or</b></i> someone at {VRCName}.<br/>
                        Please contact {VRCName} if you have further questions.
                      </span>
                    )
                  }
                </div>
                <Button
                  color="transparent"
                  rightAlignText
                  onClick={(e) => {
                    e.preventDefault();
                    requestPartnershipMutation(params)
                      .then(onSuccess)
                      .catch(alertError)
                  }}
                >
                  Request partnership
                </Button>
              </div>
            );
          }
          else if (status === STATUS.rejected) {
            return (
              <div className={classname}>
                {
                  !longMessage ? <span>Partnership <i>rejected</i></span> :
                  (
                    <span>
                      Your partnership request with <b>{VRCName}</b> has been rejected by {VRCName}.<br />
                      This could be because some of the information required hasn't been provided.<br />
                      Please contact {VRCName} to discuss.
                    </span>
                  )
                }
                <Button
                  color="transparent"
                  rightAlignText
                  onClick={(e) => {
                    e.preventDefault();
                    requestPartnershipMutation(params)
                      .then(onSuccess)
                      .catch(alertError)
                  }}
                >
                  Request partnership
                </Button>
              </div>
            );
          }
        
          return (
            <Button
              color="transparent"
              rightAlignText
              onClick={(e) => {
                e.preventDefault();
                requestPartnershipMutation(params)
                  .then(onSuccess)
                  .catch(alertError)
              }}
            >
              Request partnership
            </Button>
          );
        }
      }
    </ThemeContext.Consumer>
  )
}

PartnershipStatus.propTypes = {
  data: P.shape({
    store: P.shape({
      group: P.shape({
        partnerships: P.arrayOf(
          P.shape({
            status: P.oneOf(Object.values(STATUS)).isRequired,
            requesteeId: P.string.isRequired,
            requesterId: P.string.isRequired,
          }).isRequired,
        ).isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  requesterId: P.string.isRequired,
  requesteeId: P.string.isRequired,
  requestPartnershipMutation: P.func.isRequired,
};

export default compose(
  graphql(PARTNERSHIP_STATUS_QUERY, {
    options: ({ requesteeId, requesterId }) => ({
      variables: {
        requesteeId,
        requesterId,
      },
    }),
  }),
  graphql(REQUEST_PARTNERSHIP_MUTATION, { name: 'requestPartnershipMutation' }),
  displayLoading(),
)(PartnershipStatus);
